import angular from "angular";

import {
  AccountingTargetSettings, AccountingTargetListItem, ApiItemType, Arrays, ExportMethod, InputEnumOption, InvoicesLogic,
  OpenAPIV3, ServiceModel, ServiceModels, SitemapNode, Translations, TranslationsInstance, TranslationsDictionary,
  Usecase, UsecaseListItem, ServiceModelListItem
} from "@salaxy/core";

import { UiHelpers } from "../../services";
import { ApiCrudObjectController } from "../bases";
import { SitemapHelper } from "../../helpers";
/**
 * UI logic for viewing and adding new (modifying) service models
 * Service models define service models
 */
export class ServiceModelCrudController extends ApiCrudObjectController<ServiceModel, ServiceModelListItem> {
  isSaving: boolean;
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "ServiceModels",
    "UiHelpers",
    "$location",
    "$routeParams"
  ];

  constructor(
    private fullApi: ServiceModels,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any
  ) { // Dependency injection
    super(fullApi, uiHelpers, $location, $routeParams);
  }

  $onInit() {
    super.$onInit();
    this.initLanguage();
  }


  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/service-models",
      detailsUrl: this.detailsUrl || "/service-models/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/ServiceModels.html",
      oDataOptions: {},
    };
  }

  /**
   * Event handler for accounting target selection.
   * @param current Settings object to edit/create.
   * @param logic Logic object passed to the dialog controller.
   * @param logic.isReadOnly Read-only flag in logic.
   * @param value Boolean indicating if the item is selected or not.
   * @param item  The selected item.
   * @param allItems All selected items.
   */
  public onAccountingTargetSelect(current: AccountingTargetSettings, logic: { isReadOnly: boolean }, value: boolean, item: AccountingTargetListItem, allItems: AccountingTargetListItem[]) {
    allItems.splice(0, allItems.length);
    logic.isReadOnly = true;
    if (value == true) {
      current.id = item.id;
      current.avatar = item.otherPartyInfo.avatar;
      current.format = item.data.format;
      current.channel = item.data.channel;
      current.method = item.data.method;
      current.availableMethods = Arrays.assureArray(item.data.availableMethods) as ExportMethod[];
      logic.isReadOnly = false;
      allItems.push(item);
    }
  }

  /**
   * Event handler for usecase selection.
   * @param current Settings object to edit/create.
   * @param logic Logic object passed to the dialog controller.
   * @param logic.isReadOnly Read-only flag in logic.
   * @param value Boolean indicating if the item is selected or not.
   * @param item  The selected item.
   * @param allItems All selected items.
   */
  public onUsecaseSelect(current: Usecase, logic: { isReadOnly: boolean }, value: boolean, item: UsecaseListItem, allItems: UsecaseListItem[]) {
    allItems.splice(0, allItems.length);
    logic.isReadOnly = true;
    if (value == true) {
      current.uri = item.reference;
      current.label = item.shortText;
      current.itemType = item.entityType as ApiItemType;
      current.status = item.status;
      current.order = item.data.order;
      logic.isReadOnly = false;
      allItems.push(item);
    }
  }

  /**
   * Return Avatar from current.payment.channels ENUM
   * @returns Avatar
   */
  /* public getPaymentChannelAvatar(item) {
    return InvoicesLogic.getChannelAvatar(item.channel);
  } */

  /**
   * Created InputEnumOption [] from current.accounting.targets
   * @returns InputEnumOption [] for template
   */
  public getSelectableAccountingTargets(): InputEnumOption[] {
    return this.uiHelpers.cache(this, "selectableAccountingTargets", () => {
      return this.current.accounting.targets.map(item => ({
        text: item.avatar.displayName,
        value: item.id,
        ui: { avatar: item.avatar }
      }) as InputEnumOption);
    }, () => this.current.accounting.targets.map(item => item.id)
    );
  }

  /**
   * Created InputEnumOption [] from all payment channels
   * @returns InputEnumOption [] for template
   */
  public getSelectablePaymentChannels(): InputEnumOption[] {
    if (this.current.payments.channels) {
      return this.uiHelpers.cache(this, "selectablePaymentChannels", () => {
        return this.current.payments.channels.map(item => {
          const avatar = InvoicesLogic.getChannelAvatar(item.channel);
          return {
            text: avatar.displayName,
            value: avatar.id,
            ui: avatar
          } as InputEnumOption;
        });
      }, () => this.current.payments.channels.map(item => InvoicesLogic.getChannelAvatar(item.channel).id)
      );
    }
  }

  /** Returns all payment channels. */
  public getAllPaymentChannels(): InputEnumOption[] {
    return this.uiHelpers.cache(this, "allSelectablePaymentChannels", () => {
      return InvoicesLogic.getAllChannels().map(item => {
        return {
          text: item.displayName,
          value: item.id,
          ui: { avatar: item }
        } as InputEnumOption;
      });
    }, () => InvoicesLogic.getAllChannels().map(item => item.id)
    );
  }

  /**
   * Save service model
   */
  public saveServiceModel() {
    this.isSaving = true;
    this.commitSitemapNode();
    this.save().then(() => {
      this.isSaving = false;
    });
  }

  /**********************
   * Sitemap functionality
   **********************/

  private _sitemap: SitemapNode[];

  private _currentSitemapNode: SitemapNode;

  /** Current instance of Translations. */
  private translations: TranslationsInstance;

  /** Clean dictionary for reloading the translations */
  private cleanDictionary: TranslationsDictionary;

  /** Gets the editable version of the sitemap */
  public get sitemap(): SitemapNode[] {
    if (!this._sitemap) {
      this.translations.setTerms(this.current?.site?.texts);
      this._sitemap = SitemapHelper.getEditableSitemap(this.current.site);
      this.currentSitemapNode = this._sitemap[0];
    }
    return this._sitemap;
  }

  /** Resets all sitemap changes */
  public resetSitemapChanges(): void {
    this._sitemap = null;
    this.current.site.texts = {};
    this.current.site.sitemap = {};
  }

  /**
   * Show / hide all nodes in the sitemap.
   * @param open If true open / show all nodes. Default is close all nodes.
   */
  public showHideAll(open: boolean) {
    this.sitemap.forEach((level1) => {
      level1.$edit.$isOpen = open;
      (level1.children || []).forEach((level2) => {
        level2.$edit.$isOpen = open;
      });
    });
  }

  /** Currently edited sitemap node */
  public get currentSitemapNode(): SitemapNode {
    return this._currentSitemapNode;
  }
  public set currentSitemapNode(value) {
    this.commitSitemapNode();
    this._currentSitemapNode = value;
  }

  /** Commits the changes of the current sitemap node to current service model (no save to server.)  */
  public commitSitemapNode() {
    const node = this._currentSitemapNode;
    if (node) {
      this.current.site.sitemap = this.current.site.sitemap || {};
      const entries = Object.entries(node.$edit).filter(([key, value]) => value != null && value != "" && !(Array.isArray(value) && value.length == 0) && key[0] != "$" && !(key == "visible" || value == "default"));
      if (entries.length) {
        const objToStore = Object.fromEntries(entries);
        if (objToStore.roles) {
          // TODO: Add proper Array editor and remove this (also in getEditableSitemap()).
          objToStore.roles = Arrays.assureArray(objToStore.roles as string).map((x) => x.trim());
        }
        this.current.site.sitemap[node.$edit.$id] = objToStore;
      } else if (this.current.site.sitemap[node.$edit.$id]) {
        delete this.current.site.sitemap[node.$edit.$id];
      }
      this.translations = new TranslationsInstance(angular.copy(this.cleanDictionary));
      this.translations.setTerms(this.current?.site?.texts);
      this._sitemap = SitemapHelper.getEditableSitemap(this.current.site, this._sitemap);
    }
  }

  /**
   * Resets all changes for the current sitemap node.
   * @param resetCommitted If true, also resets the values that are committed to the
   * original values from in the library
   */
  public resetSitemapNode(resetCommitted: boolean) {
    const node = this.currentSitemapNode;
    if (resetCommitted) {
      if (this.current.site.sitemap[node.$edit.$id]) {
        delete this.current.site.sitemap[node.$edit.$id];
      }
    }
    this.translations = new TranslationsInstance(angular.copy(this.cleanDictionary));
    this.translations.setTerms(this.current?.site?.texts);
    this._sitemap = SitemapHelper.getEditableSitemap(this.current.site, this._sitemap);
  }

  /**
   * Returns true, if the node has test role set.
   * @param node Node to evaluate.
   */
  public getIsTest(node: SitemapNode): boolean {
    const roles = node.$edit.roles?.length ? node.$edit.roles : node.roles;
    return roles ? Arrays.assureArray(roles).indexOf("test") >= 0 : false;
  }


  /**
   * Gets all roles except "test" (which is displayed by getIsTest) as comma separated string.
   * @param node Node to evaluate.
   */
  public getRoles(node: SitemapNode): string {
    const roles = node.$edit.roles?.length ? node.$edit.roles : node.roles;
    return roles ? Arrays.assureArray(roles).filter((x) => x != "test").join(", ") : null;
  }

  /** Gets the params schema from the sitemap (service model). */
  public getSitemapParamsSchema(): OpenAPIV3.Document {
    return this.uiHelpers.cache(this, "sitemapNodeParams", () => {
      const node = this.currentSitemapNode;
      const result: OpenAPIV3.Document = {
        openapi: "3.0.1",
        info: {
          title: "SitemapParams_" + node.id,
          version: "1.0",
          description: `Sitemap parameters for node ${node.$edit.$id}`,
        },
        paths: {},
        components: {
          schemas: {},
        }
      };
      this.addNodeParamSchemas(this.sitemap, result.components.schemas);
      return result;
    }, () => this.currentSitemapNode?.$edit.$id);
  }

  /** Field to export / import the current object (shown in code editor). */
  public json: string;

  /** Exports the current object as json to property this.json. */
  public exportJson() {
    this.json = JSON.stringify(this.current, null, 2);
  }

  /** Sets current object from the JSON (this.json shown in code editor). */
  public importJson() {
    this._sitemap = null;
    this._currentSitemapNode = null;
    this.translations = new TranslationsInstance(angular.copy(this.cleanDictionary));
    this.translations.setTerms(this.current?.site?.texts);
    this.setCurrentValue(JSON.parse(this.json));
  }

  private addNodeParamSchemas(nodes: SitemapNode[], schemas: { [key: string]: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject }): void {
    if (!nodes?.length) {
      return;
    }
    nodes.forEach((node) => {
      if (Object.keys(node.params || {}).length) {
        const schema = angular.copy(this.defaultParamsSchema);
        schema.properties = {...schema.properties, ...this.currentSitemapNode.params};
        schemas[node.$edit.$id] = schema;
      } else {
        schemas[node.$edit.$id] = this.defaultParamsSchema;
      }
      this.addNodeParamSchemas(node.children, schemas);
    });
  }

  private defaultParamsSchema: OpenAPIV3.SchemaObject = {
    title: "Current sitemap node",
    type: "object",
    properties: {}
  };

  /**
   * Translates the given key to current language with the translations in the current service model
   * @param key The translation key
   */
  public translate(key: string): string {
    return this.translations.get(key);
  }

  /** Initializes the Translations instance (loads the language and sets the current language). */
  private initLanguage() {
    const lang = Translations.getLanguage();
    if (!this.translations) {
      this.translations = new TranslationsInstance();
      this.translations.localesPath = Translations.instance.localesPath; // Get the locales path from the current site (global instance)
    }
    this.translations.loadLanguage(lang)
      .then(() => {
        this.cleanDictionary = angular.copy(this.translations.getCurrentDictionary());
        this.translations.setLanguage(lang);
        this.translations.setTerms(this.current?.site?.texts);
      });
  }

}